import { useEffect, useEffectEvent, useLayoutEffect, useRef, useState, type ReactNode } from "react";
import { logoAnimates, LogoWaits } from "../ui/logo/clock";
import { LogoAnimation } from "../ui/logo/LogoAnimation";
import { readJson } from "../lib/storage";
import { demoStart } from "./demo";
import { supportWindow } from "./support";

const OPENED = "tafo.opened";
/** The mark stands for a moment before the till appears. */
const MARK_STANDS_MS = 400;
/** As long as the fade in base.css (.opening). */
const FADE_MS = 200;
const MODIFIERS = new Set(["Shift", "Control", "Alt", "Meta", "AltGraph", "CapsLock"]);
/** The film has this long to start; otherwise the logo is drawn by the page itself after all. */
const FILM_START_MS = 1_500;
/**
 * An empty picture to show until the film plays. Without one, the Android app shows its own grey square with a
 * play button before the logo (Furkan, 23/10/2026: "bu cikiyo tafo logosundan once").
 */
const NO_POSTER = "data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7";

/**
 * What this window keeps of its opening. In the demo it is the start of that demo (`npm run demo` in the
 * root sets VITE_DEMO_START): on a Mac, Vite reloads the tab of the till that is still open instead of
 * opening a new one, and a demo that starts again is the program that starts again (Furkan, 03/10/2026).
 */
function openingMark(): string {
  return demoStart() ?? "1";
}

/** A reload is not an opening: this window remembers that the till was opened (sessionStorage). */
function openedBefore(): boolean {
  try {
    return window.sessionStorage.getItem(OPENED) === openingMark();
  } catch {
    return false;
  }
}

function rememberOpened(): void {
  try {
    window.sessionStorage.setItem(OPENED, openingMark());
  } catch {
    // no storage (a private window): the logo is written again after a reload
  }
}

/**
 * The writing of the logo as a film (Furkan, 10/10/2026: "de welkomstanimatie is heel mooi maar hapert te
 * veel, te weinig frames"; ADR 0037). Drawn by the page itself the logo shares the computer with the till
 * starting behind it, and on a slow kassa-pc frames fell away. A film is played apart from the page, at 60
 * frames a second, whatever the till does meanwhile. It was made from the drawing of the page
 * (`scripts/render-opening.ts`), on the ground of Wit and of Zwart. A browser that does not play it draws
 * the logo itself, as before.
 */
function OpeningFilm({ theme, onEnd, onFail }: { theme: "light" | "dark"; onEnd: () => void; onFail: () => void }) {
  const film = useRef<HTMLVideoElement>(null);
  const failed = useEffectEvent(onFail);
  useLayoutEffect(() => {
    const video = film.current;
    if (video === null) return;
    // a browser that plays neither film draws the logo itself, before anything is seen
    if (video.canPlayType("video/webm") === "" && video.canPlayType("video/mp4") === "") {
      failed();
      return;
    }
    // muted, set on the element itself: only then may a phone start it without a tap
    video.muted = true;
    video.defaultMuted = true;
    let started = false;
    /** Left the screen (or set up again): an interrupted start is no failure of the film. */
    let gone = false;
    const timer = setTimeout(() => {
      if (!started) failed();
    }, FILM_START_MS);
    const playing = () => {
      started = true;
    };
    video.addEventListener("playing", playing);
    // refused (no autoplay here, the film not found): the page draws the logo after all
    Promise.resolve(video.play()).catch(() => {
      if (!started && !gone) failed();
    });
    return () => {
      gone = true;
      clearTimeout(timer);
      video.removeEventListener("playing", playing);
      video.pause();
    };
  }, []);
  return (
    <video
      ref={film}
      className="opening-film"
      role="img"
      aria-label="Tafo"
      playsInline
      preload="auto"
      poster={NO_POSTER}
      disablePictureInPicture
      onEnded={onEnd}
      onError={onFail}
    >
      <source src={`/opening-${theme}.webm`} type="video/webm" />
      <source src={`/opening-${theme}.mp4`} type="video/mp4" onError={onFail} />
    </video>
  );
}

/**
 * The theme the opening is drawn in: the one on the page (the Tafo app puts the phone's there before it
 * draws), else the one this till remembers ("Wit" or "Zwart"), which the till puts on the page only once
 * the opening is drawn.
 */
function themeOfPage(): "light" | "dark" {
  const set = document.documentElement.dataset.theme;
  if (set === "dark" || set === "light") return set;
  return readJson<string>("tafo.theme", "light") === "dark" ? "dark" : "light";
}

/** The logo is being written, the mark stands, the opening fades away, the till is there. */
type Stage = "playing" | "standing" | "leaving" | "over";

/**
 * The first opening of the till and of the Tafo app (Furkan, 02/10/2026 and 10/10/2026: "er is geen
 * welkomstanimatie in de app"; ADR 0015, 0037): the logo is written once, over the till,
 * which starts behind it and is ready when the logo is. Not after a reload, not for who asked the system
 * for less motion, and not in a support window, where Tafo opens the till of a shop to help. A tap or a
 * key ends it at once, and that tap or key does nothing else: nobody sells something by accident behind
 * the logo.
 *
 * The logo at the top of the till waits behind the opening and is written when it leaves (ADR 0016).
 */
export function Opening({ children }: { children?: ReactNode }) {
  const [stage, setStage] = useState<Stage>(() =>
    logoAnimates() && !openedBefore() && supportWindow.read() === null ? "playing" : "over",
  );
  const inFront = stage === "playing" || stage === "standing";
  /** The film, or the page drawing the logo itself when the film does not play. */
  const [drawing, setDrawing] = useState<"film" | "page">("film");
  const [theme] = useState(themeOfPage);
  const written = () => {
    setStage((now) => (now === "playing" ? "standing" : now));
  };

  useEffect(() => {
    if (stage === "playing") rememberOpened();
  }, [stage]);

  useEffect(() => {
    if (!inFront) return;
    const end = (event: KeyboardEvent) => {
      // with Ctrl, Alt or ⌘ a key is for the browser (reload, the developer tools)
      if (event.ctrlKey || event.altKey || event.metaKey || MODIFIERS.has(event.key)) return;
      event.preventDefault();
      event.stopPropagation();
      setStage("leaving");
    };
    window.addEventListener("keydown", end, true);
    return () => {
      window.removeEventListener("keydown", end, true);
    };
  }, [inFront]);

  // the mark stands, then the opening fades; should the fade never report its end (a tab in the
  // background), the opening still goes
  useEffect(() => {
    if (stage !== "standing" && stage !== "leaving") return;
    const timer = setTimeout(
      () => {
        setStage(stage === "standing" ? "leaving" : "over");
      },
      stage === "standing" ? MARK_STANDS_MS : FADE_MS + 100,
    );
    return () => {
      clearTimeout(timer);
    };
  }, [stage]);

  return (
    <LogoWaits value={inFront}>
      {children}
      {stage !== "over" && (
        <div
          className={stage === "leaving" ? "opening leaving" : "opening"}
          onPointerDown={() => {
            setStage("leaving");
          }}
          onTransitionEnd={(event) => {
            if (event.target === event.currentTarget) setStage("over");
          }}
        >
          {drawing === "film" ? (
            <OpeningFilm
              theme={theme}
              onEnd={written}
              onFail={() => {
                setDrawing("page");
              }}
            />
          ) : (
            <LogoAnimation onEnd={written} />
          )}
        </div>
      )}
    </LogoWaits>
  );
}
